import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Grid,
  Link,
  Paper,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";

export type CardSizes = {
  xs: "auto" | number | boolean;
  sm?: "auto" | number | boolean;
  md?: "auto" | number | boolean;
  lg?: "auto" | number | boolean;
  xl?: "auto" | number | boolean;
};

type Props = {
  id?: string;
  children?: ReactNode;
  loading: boolean;
  title: ReactNode;
  /*
  the titleTo prop is a URL.
  if a titleTo prop is supplied, the Title will hyperlink to the URL provided*/
  titleTo?: string;
  tagline?: ReactNode;
  icon?: ReactNode;
  sizes?: CardSizes;
  to?: string;
  PaperProps?: SxProps;
  sx?: SxProps<Theme>;
  "data-testid"?: string;
  headingLevel?: "h2" | "h3";
};

export function HubCard({
  id,
  children,
  loading,
  title,
  tagline,
  icon,
  sizes,
  to,
  titleTo,
  PaperProps,
  sx,
  headingLevel = "h2",
  ...rest
}: Props) {
  return (
    <Grid
      id={id}
      container
      {...(to ? { component: RouterLink, to: to } : {})}
      {...sizes}
      sx={[
        { position: "relative", height: "auto" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Paper
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          p: 3,
          minWidth: 0,
          width: 1,
          ...PaperProps,
        }}
        data-testid={rest["data-testid"]}
      >
        <Grid container>
          <Grid size="grow">
            <Typography variant="h5" component={headingLevel} {...titleStyling}>
              {titleTo ? (
                <Link component={RouterLink} to={titleTo}>
                  {title}
                </Link>
              ) : (
                title
              )}
            </Typography>
            {tagline && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                }}
              >
                {tagline}
              </Typography>
            )}
          </Grid>
          {icon && <Grid sx={{ p: 0.5 }}>{icon}</Grid>}
        </Grid>
        <Grid
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "start",
          }}
          size="grow"
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            children
          )}
        </Grid>
      </Paper>
    </Grid>
  );
}

const titleStyling = {
  sx: { textTransform: "uppercase", mb: -0.5, fontWeight: 700 },
};
